/**
 * Unified Diagram Controls - Context Provider
 * Issue #245: Standardize Diagram Controls Across All Renderers
 *
 * This context provides a unified state management system for all diagram controls.
 */

import { createContext, useContext, useCallback, useState, useMemo } from 'react';
import type { Node, Edge } from '@xyflow/react';
import type {
  DiagramControlsContextValue,
  DiagramControlsProviderProps,
  DiagramCapabilities,
  LayoutDirection,
  ActiveFilters,
} from './types';

// Default capabilities for diagrams
const DEFAULT_CAPABILITIES: DiagramCapabilities = {
  search: false,
  focus: false,
  filters: [],
  layoutDirection: false,
  autoLayout: false,
  legend: false,
  interactive: true,
  zoomPan: true,
};

// Create context with undefined default (we'll check for provider)
const DiagramControlsContext = createContext<DiagramControlsContextValue | undefined>(undefined);

/**
 * Provider component for diagram controls
 */
export function DiagramControlsProvider({
  children,
  initialCapabilities,
  onFitView,
  onZoomIn,
  onZoomOut,
  onAutoLayout,
}: DiagramControlsProviderProps) {
  // State
  const [zoomLevel, setZoomLevel] = useState(1);
  const [layoutDirection, setLayoutDirectionState] = useState<LayoutDirection>('TB');
  const [isLayouting, setIsLayouting] = useState(false);
  const [searchQuery, setSearchQueryState] = useState('');
  const [focusedNodeId, setFocusedNodeIdState] = useState<string | null>(null);
  const [focusDepth, setFocusDepthState] = useState(1);
  const [isFocusMode, setIsFocusMode] = useState(false);
  const [activeFilters, setActiveFilters] = useState<ActiveFilters>(new Map());
  const [totalNodeCount, setTotalNodeCount] = useState(0);
  const [totalEdgeCount, setTotalEdgeCount] = useState(0);
  const [filteredNodeCount, setFilteredNodeCount] = useState(0);
  const [filteredEdgeCount, setFilteredEdgeCount] = useState(0);
  const [capabilities, setCapabilitiesState] = useState<DiagramCapabilities>({
    ...DEFAULT_CAPABILITIES,
    ...initialCapabilities,
  });
  // View controls
  const fitView = useCallback(() => {
    onFitView?.();
  }, [onFitView]);

  const zoomIn = useCallback(() => {
    onZoomIn?.();
  }, [onZoomIn]);

  const zoomOut = useCallback(() => {
    onZoomOut?.();
  }, [onZoomOut]);

  // Layout controls
  const setLayoutDirection = useCallback((direction: LayoutDirection) => {
    setLayoutDirectionState(direction);
  }, []);

  const applyAutoLayout = useCallback(() => {
    if (onAutoLayout) {
      setIsLayouting(true);
      onAutoLayout();
      // Reset layouting state after a delay (the actual layout is handled by the renderer)
      setTimeout(() => setIsLayouting(false), 500);
    }
  }, [onAutoLayout]);

  // Search & Focus controls
  const setSearchQuery = useCallback((query: string) => {
    setSearchQueryState(query);
  }, []);

  const setFocusedNodeId = useCallback((id: string | null) => {
    setFocusedNodeIdState(id);
    if (id) {
      setIsFocusMode(true);
    }
  }, []);

  const setFocusDepth = useCallback((depth: number) => {
    setFocusDepthState(Math.max(1, Math.min(5, depth)));
  }, []);

  const toggleFocusMode = useCallback(() => {
    setIsFocusMode(prev => {
      if (prev) {
        setFocusedNodeIdState(null);
      }
      return !prev;
    });
  }, []);

  // Filtering controls
  const setFilter = useCallback((filterId: string, values: Set<string>) => {
    setActiveFilters(prev => {
      const next = new Map(prev);
      if (values.size === 0) {
        next.delete(filterId);
      } else {
        next.set(filterId, values);
      }
      return next;
    });
  }, []);

  const toggleFilterValue = useCallback((filterId: string, value: string) => {
    setActiveFilters(prev => {
      const next = new Map(prev);
      const currentValues = next.get(filterId) || new Set();
      const newValues = new Set(currentValues);

      if (newValues.has(value)) {
        newValues.delete(value);
      } else {
        newValues.add(value);
      }

      if (newValues.size === 0) {
        next.delete(filterId);
      } else {
        next.set(filterId, newValues);
      }

      return next;
    });
  }, []);

  const clearFilter = useCallback((filterId: string) => {
    setActiveFilters(prev => {
      const next = new Map(prev);
      next.delete(filterId);
      return next;
    });
  }, []);

  const clearAllFilters = useCallback(() => {
    setActiveFilters(new Map());
  }, []);

  // Stats update
  const updateStats = useCallback(
    (nodes: Node[], edges: Edge[], filteredNodes: Node[], filteredEdges: Edge[]) => {
      setTotalNodeCount(nodes.length);
      setTotalEdgeCount(edges.length);
      setFilteredNodeCount(filteredNodes.length);
      setFilteredEdgeCount(filteredEdges.length);
    },
    []
  );

  // Capabilities update
  const setCapabilities = useCallback((newCapabilities: DiagramCapabilities) => {
    setCapabilitiesState(newCapabilities);
  }, []);

  // Memoize the context value
  const contextValue = useMemo<DiagramControlsContextValue>(
    () => ({
      // State
      zoomLevel,
      layoutDirection,
      isLayouting,
      searchQuery,
      focusedNodeId,
      focusDepth,
      isFocusMode,
      activeFilters,
      totalNodeCount,
      totalEdgeCount,
      filteredNodeCount,
      filteredEdgeCount,
      capabilities,

      // Actions
      fitView,
      zoomIn,
      zoomOut,
      setZoomLevel,
      setLayoutDirection,
      applyAutoLayout,
      setSearchQuery,
      setFocusedNodeId,
      setFocusDepth,
      toggleFocusMode,
      setFilter,
      toggleFilterValue,
      clearFilter,
      clearAllFilters,
      updateStats,
      setCapabilities,
    }),
    [
      zoomLevel,
      layoutDirection,
      isLayouting,
      searchQuery,
      focusedNodeId,
      focusDepth,
      isFocusMode,
      activeFilters,
      totalNodeCount,
      totalEdgeCount,
      filteredNodeCount,
      filteredEdgeCount,
      capabilities,
      fitView,
      zoomIn,
      zoomOut,
      setLayoutDirection,
      applyAutoLayout,
      setSearchQuery,
      setFocusedNodeId,
      setFocusDepth,
      toggleFocusMode,
      setFilter,
      toggleFilterValue,
      clearFilter,
      clearAllFilters,
      updateStats,
      setCapabilities,
    ]
  );

  return (
    <DiagramControlsContext.Provider value={contextValue}>
      {children}
    </DiagramControlsContext.Provider>
  );
}

/**
 * Hook to access the diagram controls context
 */
export function useDiagramControls(): DiagramControlsContextValue {
  const context = useContext(DiagramControlsContext);
  if (!context) {
    throw new Error('useDiagramControls must be used within a DiagramControlsProvider');
  }
  return context;
}

/**
 * Hook to access the diagram controls context, returning undefined if not in provider
 * Useful for components that may or may not be within a provider
 */
export function useDiagramControlsOptional(): DiagramControlsContextValue | undefined {
  return useContext(DiagramControlsContext);
}

export { DiagramControlsContext };
